import { useState } from "react";
import type { OpsDealProductType, OpsDealSaleType, OpsDealTypeSlot } from "@/lib/opsDealTypesClient";
import { DealTypesSaleProviders } from "./DealTypesSaleProviders";
import { DealTypesMatchAliasesField } from "./DealTypesMatchAliasesField";
import { DealTypesProductSlots, type DealTypesAddSlotInput } from "./DealTypesProductSlots";
import {
  parseAllowedDepositUsd,
  parseFinanceSources,
  parseRtoLessor,
  saleTypeMetadataWithProviders,
} from "@/lib/opsDealTypes/saleTypeProviders";
import { matchAliasesWithMetadata, parseMatchAliases } from "@/lib/opsDealTypes/dealTypeMatch";
import { isFollowOnProductMetadata, metadataWithFollowOn } from "@/lib/opsDealTypes/followOnProduct";

export function DealTypesProductEditor(props: {
  product: OpsDealProductType;
  saleTypes: OpsDealSaleType[];
  linkedSaleIds: string[];
  slots: OpsDealTypeSlot[];
  onSave: (patch: {
    display_name: string;
    description: string;
    is_active: boolean;
    metadata?: Record<string, unknown>;
  }) => Promise<void>;
  onDelete: () => Promise<void>;
  onSaveLinks: (saleTypeIds: string[]) => Promise<void>;
  onAddSlot: (input: DealTypesAddSlotInput) => Promise<void>;
  onRemoveSlot: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState(props.product.display_name);
  const [description, setDescription] = useState(props.product.description);
  const [active, setActive] = useState(props.product.is_active);
  const [linkIds, setLinkIds] = useState<string[]>(props.linkedSaleIds);
  const [aliasesText, setAliasesText] = useState(parseMatchAliases(props.product.metadata).join("\n"));
  const [followOn, setFollowOn] = useState(isFollowOnProductMetadata(props.product.metadata));

  return (
    <div className="space-y-4" data-app-panel="DealTypesProductEditor">
      <div className="space-y-2">
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Display name
          <input
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <p className="text-xs text-gray-500 dark:text-zinc-400">Key: {props.product.type_key}</p>
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Description
          <textarea
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <DealTypesMatchAliasesField value={aliasesText} onChange={setAliasesText} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={followOn} onChange={(e) => setFollowOn(e.target.checked)} />
          Follow-on (warranty / repair / repo) — later deal on the same serial, not the original sale
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white dark:bg-zinc-200 dark:text-zinc-900"
            onClick={() =>
              void props.onSave({
                display_name: name,
                description,
                is_active: active,
                metadata: metadataWithFollowOn(
                  matchAliasesWithMetadata(props.product.metadata, aliasesText),
                  followOn,
                ),
              })
            }
          >
            Save product type
          </button>
          <button
            type="button"
            className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 dark:border-red-700 dark:text-red-300"
            onClick={() => void props.onDelete()}
          >
            Delete
          </button>
        </div>
      </div>

      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Sale types that apply</h4>
        {props.saleTypes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Create sale types on the Sale types tab first.</p>
        ) : (
          <ul className="space-y-1">
            {props.saleTypes.map((s) => {
              const checked = linkIds.includes(s.id);
              return (
                <li key={s.id}>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setLinkIds((prev) =>
                          checked ? prev.filter((id) => id !== s.id) : [...prev, s.id],
                        )
                      }
                    />
                    {s.display_name}
                    {!s.is_active ? " (inactive)" : ""}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
          onClick={() => void props.onSaveLinks(linkIds)}
        >
          Save sale-type links
        </button>
      </section>

      <DealTypesProductSlots
        ownerLabel="Required or allowed paperwork for this product type."
        allowPlantLayer
        slots={props.slots}
        onAddSlot={props.onAddSlot}
        onRemoveSlot={props.onRemoveSlot}
      />
    </div>
  );
}

export function DealTypesSaleEditor(props: {
  sale: OpsDealSaleType;
  slots: OpsDealTypeSlot[];
  onSave: (patch: {
    display_name: string;
    description: string;
    is_active: boolean;
    metadata?: Record<string, unknown>;
  }) => Promise<void>;
  onDelete: () => Promise<void>;
  onAddSlot: (input: DealTypesAddSlotInput) => Promise<void>;
  onRemoveSlot: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState(props.sale.display_name);
  const [description, setDescription] = useState(props.sale.description);
  const [active, setActive] = useState(props.sale.is_active);
  const [rtoLessor, setRtoLessor] = useState(parseRtoLessor(props.sale.metadata));
  const [financeSourcesText, setFinanceSourcesText] = useState(
    parseFinanceSources(props.sale.metadata).join("\n")
  );
  const [allowedDeposit, setAllowedDeposit] = useState(() => {
    const n = parseAllowedDepositUsd(props.sale.metadata);
    return n == null ? "" : String(n);
  });
  const [aliasesText, setAliasesText] = useState(parseMatchAliases(props.sale.metadata).join("\n"));

  return (
    <div className="space-y-3" data-app-panel="DealTypesSaleEditor">
      <label className="block text-sm text-gray-700 dark:text-zinc-300">
        Display name
        <input
          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <p className="text-xs text-gray-500 dark:text-zinc-400">Key: {props.sale.type_key}</p>
      <label className="block text-sm text-gray-700 dark:text-zinc-300">
        Description
        <textarea
          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <DealTypesSaleProviders
        typeKey={props.sale.type_key}
        displayName={name}
        metadata={props.sale.metadata}
        rtoLessor={rtoLessor}
        setRtoLessor={setRtoLessor}
        financeSourcesText={financeSourcesText}
        setFinanceSourcesText={setFinanceSourcesText}
      />
      <label className="block text-sm text-gray-700 dark:text-zinc-300">
        Allowed dealer deposit (USD)
        <input
          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
          type="number"
          min={0}
          step="0.01"
          value={allowedDeposit}
          onChange={(e) => setAllowedDeposit(e.target.value)}
          placeholder="1500"
        />
        <span className="mt-1 block text-xs text-gray-500">
          Org-authored. Payables uses this as the keep-amount when recording manufacturer AP. Not hardcoded.
        </span>
      </label>
      <DealTypesMatchAliasesField value={aliasesText} onChange={setAliasesText} />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white dark:bg-zinc-200 dark:text-zinc-900"
          onClick={() =>
            void props.onSave({
              display_name: name,
              description,
              is_active: active,
              metadata: matchAliasesWithMetadata(
                saleTypeMetadataWithProviders(props.sale.metadata, {
                  rto_lessor: rtoLessor,
                  finance_sources: financeSourcesText.split("\n"),
                  allowed_deposit_usd: allowedDeposit,
                }),
                aliasesText
              ),
            })
          }
        >
          Save sale type
        </button>
        <button
          type="button"
          className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 dark:border-red-700 dark:text-red-300"
          onClick={() => void props.onDelete()}
        >
          Delete
        </button>
      </div>
      <DealTypesProductSlots
        ownerLabel="Required or allowed paperwork for this sale type (Cash, RTO, Finance, …)."
        slots={props.slots}
        onAddSlot={props.onAddSlot}
        onRemoveSlot={props.onRemoveSlot}
      />
    </div>
  );
}
